import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Building2, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { devLog, devError, devWarn } from '../utils/logger';

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { login, register, user } = useAuth();
  
  const [mode, setMode] = useState(searchParams.get('mode') || 'login');
  const [userType, setUserType] = useState(searchParams.get('type') || 'buyer');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company_name: '',
    phone: '',
    location: ''
  });
  const [rememberMe, setRememberMe] = useState(true);

  useEffect(() => {
    // Only redirect if user is logged in AND we're not already redirecting
    if (user && !loading) {
      const from = location.state?.from || (user.user_type === 'supplier' ? '/dashboard' : '/marketplace');
      navigate(from, { replace: true });
    }
  }, [user, navigate, location, loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(formData.email, formData.password, rememberMe);
        toast.success('Welcome back!');
      } else {
        await register({
          ...formData,
          user_type: userType
        });
        if (userType === 'supplier') {
          // New suppliers go straight to the Wingman intro page so they see
          // the mobile companion app before getting lost in the dashboard.
          toast.success('Account created — meet your Supplier Wingman ↓', { duration: 5000 });
          navigate('/supplier-app', { replace: true });
          return;
        }
        toast.success('Account created successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/marketplace';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Debug logging
  devLog('AuthPage render', { user, loading, mode, userType });

  return (
    <div className="min-h-screen pt-24 pb-12 flex items-center justify-center bg-background" data-testid="auth-page">
      {loading && user ? (
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-accent" />
          <p className="text-muted-foreground">Redirecting...</p>
        </div>
      ) : (
      <div className="w-full max-w-md px-6">
        <div className="text-center mb-8">
          <h1 className="font-playfair text-3xl font-bold text-foreground mb-2">
            {mode === 'login' ? 'Welcome Back' : 'Join PatinaMart'}
          </h1>
          <p className="text-muted-foreground">
            {mode === 'login' 
              ? 'Sign in to access your account' 
              : 'Create an account to get started'
            }
          </p>
        </div>

        {/* User Type Selector (Register only) */}
        {mode === 'register' && (
          <div className="flex gap-4 mb-6">
            <button
              type="button"
              onClick={() => setUserType('buyer')}
              className={`flex-1 p-4 rounded-lg border transition-all ${
                userType === 'buyer'
                  ? 'border-accent/50 bg-accent/10 text-accent'
                  : 'border-border/40 text-muted-foreground hover:border-border'
              }`}
              data-testid="buyer-type-btn"
            >
              <User className="w-6 h-6 mx-auto mb-2" />
              <p className="font-medium">Buyer</p>
              <p className="text-xs opacity-70">Looking for materials</p>
            </button>
            <button
              type="button"
              onClick={() => setUserType('supplier')}
              className={`flex-1 p-4 rounded-lg border transition-all ${
                userType === 'supplier'
                  ? 'border-accent/50 bg-accent/10 text-accent'
                  : 'border-border/40 text-muted-foreground hover:border-border'
              }`}
              data-testid="supplier-type-btn"
            >
              <Building2 className="w-6 h-6 mx-auto mb-2" />
              <p className="font-medium">Supplier</p>
              <p className="text-xs opacity-70">Selling materials</p>
            </button>
          </div>
        )}

        {/* Auth Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Full Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground/50"
                  placeholder="John Doe"
                  required
                  data-testid="name-input"
                />
              </div>
              
              {userType === 'supplier' && (
                <>
                  <div>
                    <label className="text-sm text-muted-foreground mb-2 block">Company Name</label>
                    <input
                      type="text"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground/50"
                      placeholder="Salvage Co."
                      data-testid="company-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-2 block">Location</label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground/50"
                      placeholder="City, Country"
                      data-testid="location-input"
                    />
                  </div>
                </>
              )}
            </>
          )}

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground/50"
              placeholder="you@example.com"
              required
              data-testid="email-input"
            />
          </div>

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full h-12 px-4 pr-12 bg-secondary/30 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground/50"
                placeholder="••••••••"
                required
                minLength={6}
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {mode === 'login' && (
            <label className="flex items-center gap-2 cursor-pointer select-none -mt-2" data-testid="remember-me-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-border/40 bg-secondary/30 text-accent focus:ring-accent/40"
                data-testid="remember-me-checkbox"
              />
              <span className="text-sm text-muted-foreground">Stay signed in for 30 days</span>
            </label>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 btn-gold"
            data-testid="submit-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : mode === 'login' ? (
              'Sign In'
            ) : (
              'Create Account'
            )}
          </Button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-4 my-6">
          <div className="flex-1 h-px bg-border/40" />
          <span className="text-sm text-muted-foreground">or</span>
          <div className="flex-1 h-px bg-border/40" />
        </div>

        {/* Google Login */}
        <Button
          type="button"
          variant="outline"
          onClick={handleGoogleLogin}
          className="w-full h-12 border-border/40"
          data-testid="google-login-btn"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Continue with Google
        </Button>

        {/* Toggle Mode */}
        <p className="text-center mt-6 text-sm text-muted-foreground">
          {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}{' '}
          <button
            type="button"
            onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
            className="text-accent hover:underline"
            data-testid="toggle-mode-btn"
          >
            {mode === 'login' ? 'Sign up' : 'Sign in'}
          </button>
        </p>
      </div>
      )}
    </div>
  );
}
